import { Button, cn } from "@beweegbaas/ui";
import { createFileRoute, Link, useRouterState } from "@tanstack/react-router";
import { useSpinDelay } from "spin-delay";
import { LessonPageLayout } from "#/modules/les.leerling-gestuurd/components/lesson-page-layout";
import { lessonButtonClasses } from "#/modules/les.leerling-gestuurd/components/shared.lesson-button-classes";
import { getStudentById } from "#/modules/les.leerling-gestuurd/helpers.les.leerling";
import { useLeerlingGestuurdeLes } from "#/modules/les.leerling-gestuurd/use-leerling-gestuurde-les";
import { getLeerjaarComboHex } from "#/modules/util.i18n/enum-mapping";
import type { components } from "#/schema";

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/lesson/$lesson/student/$student/",
)({
  component: StudentStudentIndexPage,
});

type StudentPageParams = {
  locale: string;
  schoolyear?: string;
  school: string;
  lesson: string;
  student: string;
};

function StudentStudentIndexPage() {
  const params = Route.useParams();
  const { lesson } = useLeerlingGestuurdeLes();
  const navigation = useRouterState({
    select: (state) => ({
      status: state.status,
      pathname: state.location.pathname,
    }),
  });
  const student = getStudentById(lesson, params.student);

  const anyBeweegactiviteitHasIcon = lesson.beweegactiviteiten.some(
    (beweegactiviteit) => beweegactiviteit.icon?.svg,
  );

  const showAvatar = lesson.avatarsEnabled;

  return (
    <LessonPageLayout
      title={
        <h1 className={"relative m-0"}>
          Wat heb je gedaan,{" "}
          <span className={"relative"}>
            {showAvatar && (
              <img
                alt=""
                src={`/avatars/animals-5/${student?.avatarNummer}.svg`}
                className={cn(
                  "rounded-lg",
                  `absolute top-1/2 left-[140%] size-14 max-w-none
                translate-y-[-50%] object-contain`,
                )}
              />
            )}
            <span className="relative">{student?.name}</span>
          </span>
          ?
        </h1>
      }
      showBackButton={true}
    >
      <div className="grid grid-cols-1 gap-4">
        {lesson.beweegactiviteiten.map((beweegactiviteit) => (
          <BeweegactiviteitButton
            key={beweegactiviteit.id}
            beweegactiviteit={beweegactiviteit}
            params={params}
            navigation={navigation}
            anyBeweegactiviteitHasIcon={anyBeweegactiviteitHasIcon}
          />
        ))}

        {lesson.sociaalEmotioneelThemas.map((sociaalEmotioneelThema) => (
          <SociaalEmotioneelButton
            key={sociaalEmotioneelThema.id}
            sociaalEmotioneelThema={sociaalEmotioneelThema}
            params={params}
            navigation={navigation}
          />
        ))}
      </div>
    </LessonPageLayout>
  );
}

function BeweegactiviteitButton({
  beweegactiviteit,
  params,
  navigation,
  anyBeweegactiviteitHasIcon,
}: {
  beweegactiviteit: components["schemas"]["BeweegactiviteitLesModel"];
  params: StudentPageParams;
  navigation: { status: string; pathname: string };
  anyBeweegactiviteitHasIcon: boolean;
}) {
  const isPending = useSpinDelay(
    navigation.status === "pending" && navigation.pathname.includes(String(beweegactiviteit.id)),
  );

  return (
    <Button
      variant="reset"
      disabled={isPending}
      className={cn(lessonButtonClasses.default)}
      render={
        <Link
          to="/$locale/{-$schoolyear}/$school/lesson/$lesson/student/$student/activity/$id"
          params={{ ...params, id: beweegactiviteit.id! }}
        />
      }
      contentClassName="justify-between gap-6 grow"
    >
      <span className="flex min-w-0 items-center gap-6 text-wrap">
        {beweegactiviteit.icon?.svg && (
          <span
            className="flex size-10 shrink-0 items-center justify-center
              opacity-80 [&_svg]:size-full! [&_svg]:text-current"
            dangerouslySetInnerHTML={{
              __html: beweegactiviteit.icon.svg,
            }}
          />
        )}
        {anyBeweegactiviteitHasIcon && !beweegactiviteit.icon?.svg && (
          <span className="flex size-8 shrink-0" />
        )}
        {beweegactiviteit.name}
      </span>
      <div className="flex size-[1.7em] items-center justify-center">
        <div
          className="h-8 w-8 shrink-0 rounded-full"
          style={{ backgroundColor: getLeerjaarComboHex(beweegactiviteit.leerjaarCombo) }}
        />
      </div>
    </Button>
  );
}

function SociaalEmotioneelButton({
  sociaalEmotioneelThema,
  params,
  navigation,
}: {
  sociaalEmotioneelThema: components["schemas"]["SociaalEmotioneelThemaDetailModel"];
  params: StudentPageParams;
  navigation: { status: string; pathname: string };
}) {
  const isPending = useSpinDelay(
    navigation.status === "pending" &&
      navigation.pathname.includes(String(sociaalEmotioneelThema.id)),
  );

  return (
    <Button
      variant="reset"
      disabled={isPending}
      className={cn(lessonButtonClasses.default, "justify-between gap-6")}
      render={
        <Link
          to="/$locale/{-$schoolyear}/$school/lesson/$lesson/student/$student/social/$id"
          params={{ ...params, id: sociaalEmotioneelThema.id! }}
        />
      }
    >
      {sociaalEmotioneelThema.name}

      <IconHugeiconsBrain02 className="size-[1.7em] opacity-50" />
    </Button>
  );
}
